import { useCallback, useEffect, useRef, useState } from 'react';

import { CameraDevice } from '@components/CameraPicker';
import QrScanner from 'qr-scanner';

type UseQrCameraParams = {
  open: boolean;
  onScan: (data: string) => void;
  paused?: boolean;
  onDecodeError?: () => void;
};

export const useQrCamera = ({ open, onScan, paused = false, onDecodeError }: UseQrCameraParams) => {
  const [cameraDevices, setCameraDevices] = useState<CameraDevice[]>([]);
  const [qrScanner, setQrScanner] = useState<QrScanner | null>(null);

  // Keep the latest callbacks in refs so the video ref callback stays stable
  // and the scanner is created only once per mounted <video> element.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const onDecodeErrorRef = useRef(onDecodeError);
  onDecodeErrorRef.current = onDecodeError;

  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const clearVideoMirror = useCallback(() => {
    if (videoElementRef.current) videoElementRef.current.style.transform = '';
  }, []);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const initCamerasList = async () => {
      try {
        const camerasList = await QrScanner.listCameras();

        if (!isMounted) return;

        setCameraDevices(camerasList.map((camera) => ({ deviceId: camera.id, label: camera.label })));
      } catch (error) {
        console.error('Unable to fetch cameras list', error);
      }
    };

    void initCamerasList();

    return () => {
      isMounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!qrScanner) return;
    if (paused) {
      void qrScanner.stop();
    } else {
      void qrScanner.start().then(clearVideoMirror);
    }
  }, [paused, qrScanner, clearVideoMirror]);

  const setVideoRef = useCallback(
    (videoElement: HTMLVideoElement) => {
      videoElementRef.current = videoElement;

      const scanner = new QrScanner(
        videoElement,
        (result) => {
          if (result.data) {
            onScanRef.current(result.data);
          }
        },
        {
          highlightScanRegion: true,
          returnDetailedScanResult: true,
          onDecodeError: () => {
            onDecodeErrorRef.current?.();
          },
        },
      );

      void scanner.start().then(clearVideoMirror);
      setQrScanner(scanner);

      return () => {
        scanner.destroy();
        setQrScanner(null);
        videoElementRef.current = null;
      };
    },
    [clearVideoMirror],
  );

  const handleCameraChange = useCallback(
    (id: string) => {
      void qrScanner?.setCamera(id).then(clearVideoMirror);
    },
    [qrScanner, clearVideoMirror],
  );

  return { cameraDevices, setVideoRef, handleCameraChange };
};
